import React from 'react';
import { Link } from 'react-router-dom';
import { MedexOVALogo } from './MedexOVALogo';
import { Twitter, Linkedin, Facebook, Instagram, Mail, Phone, MapPin } from 'lucide-react';

const footerLinks = {
  Platform: [
    { label: 'AI Dispatch', href: '#ecosystem' },
    { label: 'NEMT Infrastructure', href: '#ecosystem' },
    { label: 'Medex Pay', href: '#products' },
    { label: 'Group Insurance', href: '#products' },
    { label: 'NEMT Vision', href: '#products' },
  ],
  Solutions: [
    { label: 'Healthcare Providers', href: '#solutions' },
    { label: 'Drivers', href: 'https://ovadispatch.com', external: true },
    { label: 'Transportation Companies', href: '#solutions' },
    { label: 'Cities & Government', href: '#solutions' },
  ],
  Company: [
    { label: 'About MedexOVA', href: '#' },
    { label: 'Careers', href: '#' },
    { label: 'Press', href: '#' },
    { label: 'Contact', href: '#contact' },
    { label: 'Partner Portal', href: '/partner', isRoute: true },
  ],
  Legal: [
    { label: 'Privacy Policy', href: '/privacy-policy', isRoute: true },
    { label: 'Terms of Service', href: '/terms-of-service', isRoute: true },
    { label: 'Cookie Policy', href: '#' },
    { label: 'Admin Login', href: '/admin', isRoute: true },
  ],
};

const socialLinks = [
  { icon: Twitter, href: '#', label: 'Twitter' },
  { icon: Linkedin, href: '#', label: 'LinkedIn' },
  { icon: Facebook, href: '#', label: 'Facebook' },
  { icon: Instagram, href: '#', label: 'Instagram' },
];

export const Footer = () => {
  return (
    <footer className="relative bg-[#0a0f1a] border-t border-white/10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {/* Brand Column */}
          <div className="col-span-2">
            <MedexOVALogo size="default" className="mb-6" />
            <p className="text-slate-400 text-sm mb-6 max-w-xs">
              The first AI-powered mobility and healthcare infrastructure platform. Unifying transportation, payments, insurance, and AI.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3 text-sm">
              <a href="mailto:contact@medexova.com" className="flex items-center space-x-2 text-slate-400 hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
                <span>contact@medexova.com</span>
              </a>
              <a href="tel:+18005551234" className="flex items-center space-x-2 text-slate-400 hover:text-white transition-colors">
                <Phone className="w-4 h-4" />
                <span>1-800-555-1234</span>
              </a>
            </div>
            
            {/* Social Links */}
            <div className="flex items-center space-x-4 mt-6">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Link Columns */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className="font-heading font-semibold text-white mb-4">{category}</h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.label}>
                    {link.isRoute ? (
                      <Link
                        to={link.href}
                        className="text-sm text-slate-400 hover:text-white transition-colors"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        target={link.external ? '_blank' : undefined}
                        rel={link.external ? 'noopener noreferrer' : undefined}
                        className="text-sm text-slate-400 hover:text-white transition-colors"
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} MedexOVA. All rights reserved.
          </p>
          <p className="text-sm text-slate-500">
            Built with{' '}
            <span className="text-neon-cyan">AI</span> for the future of healthcare mobility
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
